import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { markCompanyPinned } from '@/domain/companies/companiesActions';
import { searchTextSelector, searchTypeSelector } from '@/domain/companies/companiesSelector';
import { rtlEnable } from '@/domain/env/envSelector';
import elements from '@/components/elements';

import {
  CardBadge,
  CardPendingApprovalBadge,
  CardActionArea,
  CardActions,
  CardActionAreaOverlay,
} from '@/pages/companies/components/TileView/Card/StyledComponents';
import RouterLink from '@/components/mui/Router/RouterLink';
import Tooltip from '@/components/mui/Tooltip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import ChatBubbleTwoToneIcon from '@mui/icons-material/ChatBubbleTwoTone';
import InsertDriveFileTwoToneIcon from '@mui/icons-material/InsertDriveFileTwoTone';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import useGetCompanyPathTo from '@/pages/companies/hooks/useGetCompanyPathTo';

const mapPropsToState = (state) => ({
  isRtl: rtlEnable(state),
  searchText: searchTextSelector(state),
  searchType: searchTypeSelector(state),
});

const TileCard = ({ data: { list, gap, columnCount }, columnIndex, rowIndex, style }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [isRaised, setIsRaised] = useState(false);

  const entry = list.get(rowIndex * columnCount + columnIndex);

  const { isRtl } = useSelector(mapPropsToState);
  const pathTo = useGetCompanyPathTo(entry?.get('isConnectedToErp') ?? false, entry?.id);

  if (!entry) return null;

  const isErpConnected = entry.get('isConnectedToErp');
  const { notifications } = entry;

  const gapSide = isRtl ? 'right' : 'left';

  const onMouseOver = () => setIsRaised(true);
  const onMouseOut = () => setIsRaised(false);

  const onClickFavorite = () => {
    dispatch(markCompanyPinned({ params: { id: entry.id } }));
  };

  const isTooMuchApprovals = entry.pendingApproval >= 99;
  const isTooMuchUnprocessed = entry.unprocessed >= 99;
  const isTooMuchNotifications = notifications >= 99;
  const isTooMuchContent = isTooMuchApprovals || isTooMuchUnprocessed || isTooMuchNotifications;

  return (
    <Card
      raised={isRaised}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseOut}
      data-element={elements.content.companies.item}
      style={{
        ...style,
        height: style.height - gap,
        [`${gapSide}`]: columnIndex ? style[gapSide] + gap * columnIndex : style[gapSide],
      }}
    >
      <CardActionArea component={RouterLink} to={pathTo}>
        <CardMedia
          component="img"
          height="120"
          image={entry.get('logo')}
          alt="Company logo"
          loading="lazy"
          sx={{ objectFit: 'contain' }}
        />
        {!isErpConnected && (
          <CardActionAreaOverlay>
            {isRaised && (
              <Button sx={{ margin: 'auto' }}>
                {formatMessage({
                  id: 'button.settings',
                  defaultMessage: 'Settings',
                })}
              </Button>
            )}
          </CardActionAreaOverlay>
        )}
        <CardContent sx={{ position: 'relative', zIndex: 0 }}>
          <Typography variant="subtitle1" textAlign="center" noWrap>
            <Tooltip t={entry.get('cname')}>
              <span>{entry.get('cname')}</span>
            </Tooltip>
          </Typography>
        </CardContent>
      </CardActionArea>
      <CardActions>
        <Box display="flex" alignItems="center" gap={isTooMuchContent ? 2.5 : 2} mr={1}>
          {isErpConnected ? (
            <>
              {notifications > 0 && (
                <CardBadge
                  badgeContent={notifications}
                  color="primary"
                  slotProps={{ badge: { sx: { right: isTooMuchNotifications ? -10 : -4 } } }}
                >
                  <ChatBubbleTwoToneIcon color="primary" />
                </CardBadge>
              )}
              {entry.unprocessed > 0 && (
                <CardBadge
                  badgeContent={entry.unprocessed}
                  max={99}
                  color="primary"
                  slotProps={{ badge: { sx: { right: isTooMuchUnprocessed ? -6 : -4 } } }}
                >
                  <InsertDriveFileTwoToneIcon color="primary" />
                </CardBadge>
              )}
              {entry.pendingApproval > 0 && (
                <Tooltip
                  t={formatMessage({
                    id: 'companies.companyItem.pendingApproval.tooltip',
                    defaultMessage: 'Documents pending approval',
                  })}
                >
                  <CardPendingApprovalBadge
                    slotProps={{ badge: { sx: { right: isTooMuchApprovals ? -8 : -4 } } }}
                    badgeContent={entry.pendingApproval}
                    max={99}
                  >
                    <PublishedWithChangesOutlinedIcon color="warning" />
                  </CardPendingApprovalBadge>
                </Tooltip>
              )}
            </>
          ) : (
            <Tooltip
              t={formatMessage({
                id: 'companies.companyItem.erpIsNotConnected.tooltip',
                defaultMessage: 'ERP is not connected',
              })}
            >
              <ErrorOutlinedIcon color="error" />
            </Tooltip>
          )}
        </Box>
        <Rating max={1} value={entry.get('pinned') ? 1 : 0} onChange={onClickFavorite} sx={{ ml: 'auto' }} />
      </CardActions>
    </Card>
  );
};

export default TileCard;
